/**
 * Audit Client
 * Audit log operations for compliance and security tracking
 */

import type { AuditLogFilters, AuditLogListResponse } from './types';
import { API_URL } from './constants';
import { createHeaders, handleApiError, logApiCall } from './base-client';

// =============================================================================
// Audit Operations
// =============================================================================

/**
 * Get audit logs (SUPERVISOR, ADMIN, COMPLIANCE only)
 * Audit logs track operator actions for compliance and security.
 *
 * @param filters - Optional filters for the query
 * @returns Paginated audit log list
 *
 * @example
 * ```ts
 * const { logs, total } = await getAuditLogs({
 *   operatorId: 'op-001',
 *   action: 'VIEW_CONVERSATION',
 *   page: 1,
 *   limit: 50
 * });
 * ```
 */
export async function getAuditLogs(
  filters?: AuditLogFilters
): Promise<AuditLogListResponse> {
  const startTime = Date.now();
  const defaultResponse: AuditLogListResponse = {
    logs: [],
    total: 0,
    page: 1,
    limit: 50,
  };

  try {
    const params = new URLSearchParams();

    if (filters?.operatorId) params.append('operatorId', filters.operatorId);
    if (filters?.action) params.append('action', filters.action);
    if (filters?.resourceType) params.append('resourceType', filters.resourceType);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    params.append('page', String(filters?.page || 1));
    params.append('limit', String(filters?.limit || 50));

    const response = await fetch(
      `${API_URL}/api/admin/audit-logs?${params.toString()}`,
      { headers: createHeaders() }
    );

    logApiCall('/api/admin/audit-logs', 'GET', Date.now() - startTime, response.status);

    if (!response.ok) {
      if (response.status === 403) {
        return defaultResponse;
      }
      throw new Error(`API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    handleApiError(error, 'getAuditLogs');
    return defaultResponse;
  }
}

/**
 * Get audit logs for a specific operator
 *
 * @param operatorId - Operator ID
 * @param page - Page number
 * @param limit - Items per page
 * @returns Audit logs for the operator
 */
export async function getOperatorAuditLogs(
  operatorId: string,
  page = 1,
  limit = 50
): Promise<AuditLogListResponse> {
  return getAuditLogs({ operatorId, page, limit });
}

/**
 * Get audit logs by action type
 *
 * @param action - Action type to filter by
 * @param page - Page number
 * @param limit - Items per page
 * @returns Audit logs with the specified action
 */
export async function getAuditLogsByAction(
  action: string,
  page = 1,
  limit = 50
): Promise<AuditLogListResponse> {
  return getAuditLogs({ action, page, limit });
}

/**
 * Get audit logs by resource type
 *
 * @param resourceType - Resource type to filter by
 * @param page - Page number
 * @param limit - Items per page
 * @returns Audit logs for the resource type
 */
export async function getAuditLogsByResourceType(
  resourceType: string,
  page = 1,
  limit = 50
): Promise<AuditLogListResponse> {
  return getAuditLogs({ resourceType, page, limit });
}

/**
 * Get audit logs within a date range
 *
 * @param dateFrom - Start date (ISO string)
 * @param dateTo - End date (ISO string)
 * @param page - Page number
 * @param limit - Items per page
 * @returns Audit logs within the date range
 */
export async function getAuditLogsByDateRange(
  dateFrom: string,
  dateTo: string,
  page = 1,
  limit = 50
): Promise<AuditLogListResponse> {
  return getAuditLogs({ dateFrom, dateTo, page, limit });
}

/**
 * Get recent audit logs (last 24 hours)
 *
 * @param page - Page number
 * @param limit - Items per page
 * @returns Recent audit logs
 */
export async function getRecentAuditLogs(
  page = 1,
  limit = 50
): Promise<AuditLogListResponse> {
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  return getAuditLogs({
    dateFrom: yesterday.toISOString(),
    dateTo: now.toISOString(),
    page,
    limit,
  });
}
